// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import EquipmentActions from 'Ui/components/common/data-table/equipment-actions';
import EquipmentName from 'OnEquip/equipment/common/data-table/equipment-name';
import Status from 'Ui/components/common/data-table/status';
import TelematicsEnabled from 'Ui/components/common/data-table/telematics-enabled';
import {useEquipmentContext} from 'OnEquip/equipment/context/equipment-context';
import {MAINTENANCE} from 'OnEquip/equipment/constants/equipment-details-tabs';
import equipmentStatus from 'Common/constants/equipment-status';
import {FLEET} from 'Common/constants/routes';
import {getFleetByMembershipId, getFleetEquipment} from 'Services/membership-service';
import {fetchEffectData} from 'Utils/react-utils';
import {redirect} from 'Utils/redirect-utils';
import {formatNumberOrDefault, formatStatus, formatTimeOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {formatModelName} from 'Ui/models/equipment-model';
import {openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';
import {dateCompare, numericCompare} from 'Ui/models/maintenance';
import PartsCatalogLink from 'Ui/components/common/data-table/parts-catalog-link';
import {CATEGORY, ACTIONS, LABELS} from 'Utils/analytics-utils';
import {
    getFilterComponent,
    getFleetLegend
} from 'Ui/features/onequip/equipment/fleet/fleet-table-headers';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {exportFleetReport} from 'Services/excel-service';
import {EDIT_EQUIPMENT_LIST, MANAGE_EQUIPMENT_SETTINGS, EDIT_SERVICE, VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import {translateEquipmentArea} from 'Utils/translation-utils';
import {Box, Tooltip} from '@deere/isg.component-library';
import {isShopDeereUrlEnabled} from 'Src/ui-core/pages/onequip/parts/inventory';
import {IconAdd, IconGear, IconHistory, IconWdt, IconWrench} from '@deere/icons';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {
    MAINTENANCE_MANAGER_PROGOLF
} from 'Common/constants/isg-toggles';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

const FLEET_SEARCH_FIELDS = [
    'formattedName',
    'equipmentType',
    'manufacturerName',
    'modelDescription',
    'serialNumber'
];

function getOnEquipUrl(membershipId) {
    return `${window.props.onLinkLegacyUri}/onequip/#m/${membershipId}`;
}

function getDataGridColumns(translations, membershipId, updateFleetEquipment, history, country, isFleetToMaintenanceEnabled) {
    const onequipUrl = getOnEquipUrl(membershipId);

    return [
        {
            headerName: '',
            field: 'lastReportedMtgId',
            resizable: false,
            width: 30,
            renderCell: (params) => {
                return (
                    <TelematicsEnabled equipment={params.row}/>
                );
            }
        },
        {
            headerName: translations.AREA,
            field: 'equipmentArea',
            minWidth: 130,
            flex: 1
        },
        {
            headerName: translations.NAME,
            field: 'formattedName',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                const equipmentUrl = `${onequipUrl}/fleet/{0}/equipment/{1}`;

                return (
                    <Tooltip
                        placement='top'
                        title={params.row.serialNumber}
                    >
                        <Box
                            width='fit-content'
                        >
                            <EquipmentName
                                equipment={params.row}
                                requiredMyJdPermissions={[VIEW_EQUIPMENT_DETAILS]}
                                tab={MAINTENANCE}
                                type={FLEET}
                                url={equipmentUrl}
                            />
                        </Box>
                    </Tooltip>
                );
            }
        },
        {
            headerName: translations.TYPE,
            flex: 1,
            minWidth: 100,
            field: 'equipmentType'
        },
        {
            headerName: translations.BRAND,
            flex: 1,
            minWidth: 130,
            field: 'manufacturerName'
        },
        {
            headerName: translations.MODEL,
            flex: 1,
            minWidth: 130,
            field: 'modelDescription',
            renderCell: (params) => {
                const {row: equipment} = params;

                return (
                    <PartsCatalogLink
                        analyticsEventData={{
                            category: CATEGORY.PARTS_CATALOG,
                            label: LABELS.MODEL_CODE,
                            action: ACTIONS.FLEET_MODEL,
                            value: equipment.modelCode
                        }}
                        manufacturerId={equipment.manufacturerId}
                        url={isShopDeereUrlEnabled({
                            country,
                            isMigrated: true
                        }) ?
                            `https://shop.deere.com/us/search?text=${equipment.modelDescription}` +
                            `&partAttributes_machineModelData.models.raw=${equipment.modelDescription}` :
                            `https://partscatalog.deere.com/jdrc/search/type/model/term/${equipment.modelDescription}`}
                        value={equipment.modelDescription}
                    />
                );
            }
        },
        {
            headerName: translations.STATUS,
            flex: 1,
            minWidth: 130,
            field: 'formattedStatus',
            renderCell: (params) => {
                return (
                    <Status
                        formattedStatus={params.row.formattedStatus}
                        status={params.row.status}
                    />
                );
            }
        },
        {
            headerName: translations.ONLINK_LAST_ENGINE_HOURS_UPDATE,
            field: 'timeLastMeterReading',
            flex: 1,
            minWidth: 255,
            sortComparator: dateCompare,
            renderCell: (params) => {
                return params.row.formattedTimeLastMeterReading;
            }
        },
        {
            headerName: translations.HOURS,
            field: 'totalHours',
            type: 'number',
            flex: 1,
            minWidth: 100,
            sortComparator: numericCompare,
            renderCell: (params) => {
                return params.row.formattedTotalHours;
            }
        },
        !isFleetToMaintenanceEnabled && {
            headerName: '',
            field: 'actions',
            resizable: false,
            sortable: false,
            width: 100,
            renderCell: (params) => {
                return (
                    <EquipmentActions
                        equipment={params.row}
                        history={history}
                        translations={translations}
                        updateFleetEquipment={updateFleetEquipment}
                    />
                );
            }
        }
    ].filter(Boolean);
}

function getNavBarActions(onAddEquipmentClick, loading, fleet, translations, openBulkEngineHoursDialog, history, addToast, isFleetToMaintenanceEnabled) {
    return [
        !isFleetToMaintenanceEnabled && {
            onClick: onAddEquipmentClick,
            title: 'EQUIPMENT',
            Icon: <IconAdd/>,
            variant: 'primary',
            disabled: loading,
            requiredMyJdPermissions: EDIT_EQUIPMENT_LIST
        },
        {
            onClick: () => exportFleetReport(fleet, translations, addToast),
            title: 'EXPORT',
            Icon: <IconWdt/>,
            disabled: loading,
            requiredMyJdPermissions: EDIT_EQUIPMENT_LIST
        },
        !isFleetToMaintenanceEnabled && {
            onClick: openBulkEngineHoursDialog,
            title: 'ONLINK_UPDATE_HOURS',
            Icon: <IconHistory/>,
            disabled: loading,
            requiredMyJdPermissions: EDIT_EQUIPMENT_LIST
        },
        !isFleetToMaintenanceEnabled && {
            title: 'ONLINK_SERVICE',
            Icon: <IconWrench/>,
            onClick: () => history.push(`${FLEET}/serviceForm`),
            requiredMyJdPermissions: EDIT_SERVICE
        },
        !isFleetToMaintenanceEnabled && {
            onClick: () => redirect(`${FLEET}/settings/fleet`, history),
            title: 'SETTINGS',
            Icon: <IconGear/>,
            requiredMyJdPermissions: MANAGE_EQUIPMENT_SETTINGS
        }
    ].filter(Boolean);
}

function initializeState() {
    const [fleet, setFleet] = React.useState(() => []);
    const [loading, setLoading] = React.useState(() => true);
    const [showArchived, setShowArchived] = React.useState(() => false);

    return {
        fleet,
        setFleet,
        loading,
        setLoading,
        showArchived,
        setShowArchived
    };
}

function Fleet(props) {
    const {
        history,
        membershipId,
        openBulkEngineHoursDialog: openBulkEngineHours,
        openEquipmentCreateDialog,
        translations,
        country,
        isgToggles
    } = props;

    const {
        fleet,
        setFleet,
        loading,
        setLoading,
        showArchived,
        setShowArchived
    } = initializeState();

    const isFleetToMaintenanceEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];

    const equipmentContext = useEquipmentContext();
    const membershipFleetId = React.useRef();
    const {addToast} = useToast();

    equipmentContext.current = async (isMounted = () => true) => {
        setLoading(true);

        const {equipment} = await getFleetEquipment();

        if (isMounted()) {
            const formatted = equipment.map((equipment) => {
                const formattedName = equipment.fleetId !== equipment.fleetInUseId && equipment.fleetName ?
                    `${equipment.equipmentName} [${equipment.fleetName}]` :
                    equipment.equipmentName;

                return {
                    ...equipment,
                    equipmentArea: translateEquipmentArea(translations, equipment.equipmentArea) || translations.ONLINK_UNASSIGNED,
                    formattedTotalHours: formatNumberOrDefault(equipment.totalHours, 1),
                    formattedName,
                    modelDescription: formatModelName(equipment),
                    formattedStatus: formatStatus(equipment, translations),
                    formattedTimeLastMeterReading: formatTimeOrDefault(equipment.timeLastMeterReading)
                };
            });

            setFleet(formatted);
            setLoading(false);
        }
    };

    function openBulkEngineHoursDialog() {
        openBulkEngineHours({
            addToast,
            membershipId,
            translations,
            title: translations.ONLINK_BULK_ENGINE_HOUR_UPDATE,
            updateFleetEquipment: equipmentContext.current
        });
    }

    function onAddEquipmentClick() {
        openEquipmentCreateDialog({
            fleetId: membershipFleetId.current,
            updateFleetEquipment: equipmentContext.current
        });
    }

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const {fleets} = await getFleetByMembershipId(membershipId);

        membershipFleetId.current = fleets[0].fleetId;

        await equipmentContext.current(isMounted);
    }), [membershipId]);

    const filteredFleet = showArchived ?
        fleet :
        fleet.filter((equipment) => equipment.status !== equipmentStatus.archived);

    useNavBarActions(getNavBarActions(
        onAddEquipmentClick,
        loading,
        fleet,
        translations,
        openBulkEngineHoursDialog,
        history,
        addToast,
        isFleetToMaintenanceEnabled
    ));

    const fleetNoResultsMessage = translations.NO_RESULTS_FOUND || translations.NO_LOADED_DATA_AVAILABLE;

    return (<DataGridWrapper
        columns={getDataGridColumns(translations, membershipId, equipmentContext.current, history, country, isFleetToMaintenanceEnabled)}
        filterComponent={
            getFilterComponent(
                showArchived,
                setShowArchived,
                translations
            )
        }
        getRowId={(row) => row.equipmentId}
        initialState={{
            sorting: {
                sortModel: [
                    {
                        field: 'equipmentArea',
                        sort: 'asc'
                    }
                ]
            }
        }}
        legendComponent={getFleetLegend(translations)}
        loading={loading}
        noDataAvailableMessage={fleetNoResultsMessage}
        rows={filteredFleet}
        searchFields={FLEET_SEARCH_FIELDS}
        searchable={true}
        slotProps={{
            root: {
                disableColumnMenu: true
            }
        }}
        translations={translations}
    />);
}

Fleet.propTypes = {
    country: PropTypes.string,
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    membershipId: PropTypes.string,
    openBulkEngineHoursDialog: PropTypes.func,
    openEquipmentCreateDialog: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membershipId: state.membership.membershipId,
        country: state.membership.country,
        isgToggles: state.account.isgToggles
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openBulkEngineHoursDialog(props) {
            dispatch(openDialog(dialogTypes.BULK_ENGINE_HOURS_DIALOG, props));
        },
        openEquipmentCreateDialog(props) {
            dispatch(openDialog(dialogTypes.EQUIPMENT_CREATE_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(withRouter(Fleet)));
